import { Link } from 'react-router-dom';
import { Home, LandPlot, Key, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Stats {
  houses_for_sale_count: number;
  houses_for_sale_min_price: number | null;
  houses_for_sale_max_price: number | null;
  plots_for_sale_count: number;
  plots_for_sale_min_price: number | null;
  plots_for_sale_max_price: number | null;
  houses_for_rent_count: number;
  houses_for_rent_min_price: number | null;
  houses_for_rent_max_price: number | null;
}

function fmtRange(min: number | null, max: number | null) {
  if (min == null && max == null) return '—';
  const f = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return n.toFixed(0);
  };
  if (min == null) return `${f(max!)} ETB`;
  if (max == null) return `${f(min)} ETB`;
  if (min === max) return `${f(min)} ETB`;
  return `${f(min)} – ${f(max)} ETB`;
}

export function HighlightsOverviewCard({
  stats,
  cityQuery,
  subCityQuery,
}: {
  stats?: Stats | null;
  cityQuery: string;
  subCityQuery: string;
}) {
  const buyHref = `/buy?city=${cityQuery}&subCity=${subCityQuery}`;
  const rentHref = `/rent?city=${cityQuery}&subCity=${subCityQuery}`;
  const plotsHref = `/plots?city=${cityQuery}&subCity=${subCityQuery}`;

  const rows = [
    { icon: Home, label: 'House Prices', sub: 'For Sale', range: fmtRange(stats?.houses_for_sale_min_price ?? null, stats?.houses_for_sale_max_price ?? null), count: stats?.houses_for_sale_count ?? 0, link: buyHref, linkLabel: 'Houses' },
    { icon: LandPlot, label: 'Plots Prices', sub: 'For Sale', range: fmtRange(stats?.plots_for_sale_min_price ?? null, stats?.plots_for_sale_max_price ?? null), count: stats?.plots_for_sale_count ?? 0, link: plotsHref, linkLabel: 'Plots' },
    { icon: Key, label: 'House Prices', sub: 'For Rent', range: fmtRange(stats?.houses_for_rent_min_price ?? null, stats?.houses_for_rent_max_price ?? null), count: stats?.houses_for_rent_count ?? 0, link: rentHref, linkLabel: 'Houses' },
  ];

  return (
    <div className="rounded-lg border bg-card p-5">
      <h3 className="font-semibold text-base mb-4">Highlights & Overview</h3>
      <div className="space-y-4">
        {rows.map((r) => (
          <div key={r.label + r.sub} className="flex items-start justify-between gap-3 pb-4 border-b last:border-b-0 last:pb-0">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <r.icon className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-medium">{r.label}</div>
                <div className="text-xs text-muted-foreground">{r.sub}</div>
                <Link to={r.link} className="text-xs text-primary hover:underline inline-flex items-center gap-0.5 mt-1">
                  {r.count}+ {r.linkLabel} <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
            <div className="text-sm font-semibold text-right whitespace-nowrap">{r.range}</div>
          </div>
        ))}
      </div>
      <Button asChild variant="outline" className="w-full mt-4">
        <Link to={buyHref}>
          Explore more Prices <ChevronRight className="h-4 w-4 ml-1" />
        </Link>
      </Button>
    </div>
  );
}
